import { Box, Button, ButtonGroup, Grid, Input, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useToast, Flex } from "@chakra-ui/react";
import { BarChart } from "@mui/x-charts/BarChart";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaFileDownload } from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import Layout from "../../../components/Layout";
import { useCompany } from "../../../contexts/CompanyContext";
import {
  CommercialBillingDashboard,
  CommercialBillingParams,
  CommercialClientManagement,
  getCommercialBilling,
  getCommercialClients,
} from "../../../services/gestaoCommercialService";
import { crm } from "../../../theme";
import { csvNumber, downloadCsv } from "./shared/exportCsv";
import {
  EmptyState,
  FieldLabel,
  KpiTile,
  NO_DATA_HINT,
  PageHeader,
  Panel,
  RankBar,
  brl,
  brl0,
  fmtDate,
  monthLabel,
  num,
  pctFmt,
  selectStyle,
  tableCellStyle,
  tableHeadStyle,
} from "./shared/ui";

type Period = "year" | "12m" | "all" | "custom";

const periodLabels: { key: Period; label: string }[] = [
  { key: "year", label: "Ano atual" },
  { key: "12m", label: "Últimos 12M" },
  { key: "all", label: "Histórico" },
  { key: "custom", label: "Personalizado" },
];

const NET_COLOR = "#0891b2";

const Comercial = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const toast = useToast();
  const { currentCompany, currentEmulation } = useCompany();

  // ── Faturamento ──
  const [period, setPeriod] = useState<Period>("year");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // Filtros por nome valem só para o cliente emulado em que foram escolhidos: trocar a emulação
  // os zera já no mesmo render (sem requisição extra com o filtro antigo).
  const [clientFilter, setClientFilter] = useState({ value: "", emulation: currentEmulation });
  const [agentFilter, setAgentFilter] = useState({ value: "", emulation: currentEmulation });
  const client = clientFilter.emulation === currentEmulation ? clientFilter.value : "";
  const agent = agentFilter.emulation === currentEmulation ? agentFilter.value : "";
  const setClient = (value: string) => setClientFilter({ value, emulation: currentEmulation });
  const setAgent = (value: string) => setAgentFilter({ value, emulation: currentEmulation });
  const [data, setData] = useState<CommercialBillingDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const load = async () => {
    const id = ++seq.current;
    setLoading(true);
    try {
      const params: CommercialBillingParams = {
        period,
        from: period === "custom" && from ? from : undefined,
        to: period === "custom" && to ? to : undefined,
        client: client || undefined,
        agent: agent || undefined,
        type: currentCompany && currentCompany >= 1 && currentCompany <= 4 ? currentCompany : undefined,
        companyId: currentEmulation || undefined,
      };
      const result = await getCommercialBilling(params);
      if (id !== seq.current) return;
      setData(result);
    } catch (error) {
      if (id !== seq.current) return;
      console.log("error", error);
      toast({ title: "Não foi possível carregar o faturamento.", status: "error" });
    } finally {
      if (id === seq.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (period === "custom" && (!from || !to)) return;
    load();
  }, [period, from, to, client, agent, currentCompany, currentEmulation]);

  // ── Gestão de clientes ──
  const [mgmt, setMgmt] = useState<CommercialClientManagement | null>(null);
  const [mgmtLoading, setMgmtLoading] = useState(true);
  const [mgmtSearch, setMgmtSearch] = useState("");

  useEffect(() => {
    let stale = false;
    setMgmtLoading(true);
    (async () => {
      try {
        const result = await getCommercialClients(currentEmulation || undefined);
        if (!stale) setMgmt(result);
      } catch (error) {
        if (stale) return;
        console.log("error", error);
        toast({ title: "Não foi possível carregar a gestão de clientes.", status: "error" });
      } finally {
        if (!stale) setMgmtLoading(false);
      }
    })();
    return () => {
      stale = true;
    };
  }, [currentEmulation]);

  const mgmtRows = useMemo(() => {
    const term = mgmtSearch.trim().toLowerCase();
    const rows = mgmt?.rows ?? [];
    if (!term) return rows;
    return rows.filter((r) =>
      [r.client, r.commercialResponsible, r.agent1, r.agent2].some((v) => (v ?? "").toLowerCase().includes(term))
    );
  }, [mgmt, mgmtSearch]);

  const exportPerformance = () => {
    if (!data) return;
    downloadCsv(
      `desempenho-por-cliente-${data.from.slice(0, 10)}-${data.to.slice(0, 10)}`,
      ["Cliente", "Honorário bruto", "Honorário líquido", "VMLD", "Processos", "Ticket médio", "Participação (%)", "Vs ano anterior (%)"],
      data.clients.map((c) => [
        c.client,
        csvNumber(c.gross),
        csvNumber(c.net),
        csvNumber(c.vmld),
        c.processes,
        csvNumber(c.averageTicket),
        csvNumber(c.participation),
        c.vsPreviousYear === null ? "" : csvNumber(c.vsPreviousYear),
      ])
    );
  };

  const exportClients = () => {
    downloadCsv(
      "gestao-de-clientes",
      ["Cliente", "Comercial responsável", "Agente 1", "Agente 2", "Último faturamento"],
      mgmtRows.map((r) => [r.client, r.commercialResponsible, r.agent1, r.agent2, r.lastBilling ? fmtDate(r.lastBilling) : ""])
    );
  };

  const maxTop = Math.max(0, ...(data?.topClients ?? []).map((c) => c.gross));
  const maxAgent = Math.max(0, ...(data?.byAgent ?? []).map((a) => a.gross));

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="auto">
      <Layout>
        <Box p="24px" bg={crm.pageBg} minH="100%">
          <PageHeader title="Comercial" subtitle="Faturamento de honorários e gestão da carteira de clientes." />

          <Tabs variant="unstyled" isLazy={false}>
            <TabList mb="20px" gap="8px" flexWrap="wrap">
              {["Faturamento", "Gestão de clientes"].map((t) => (
                <Tab
                  key={t}
                  borderRadius="10px"
                  px="16px"
                  py="8px"
                  fontWeight="600"
                  fontSize="sm"
                  color={crm.textMuted}
                  bg={crm.surface}
                  border={`1px solid ${crm.border}`}
                  _selected={{ color: "white", bg: crm.brand, borderColor: crm.brand }}
                >
                  {t}
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {/* ───────── Faturamento ───────── */}
              <TabPanel p="0">
                <Flex gap="16px" mb="20px" flexWrap="wrap" align="flex-end">
                  <Box>
                    <FieldLabel>Período</FieldLabel>
                    <ButtonGroup isAttached size="sm" variant="outline">
                      {periodLabels.map((p) => (
                        <Button
                          key={p.key}
                          onClick={() => setPeriod(p.key)}
                          bg={period === p.key ? crm.brandSoft : crm.surface}
                          color={period === p.key ? crm.brand : crm.text}
                          borderColor={period === p.key ? crm.brand : crm.border}
                          h="36px"
                        >
                          {p.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Box>
                  {period === "custom" && (
                    <>
                      <Box>
                        <FieldLabel>De</FieldLabel>
                        <Input type="date" size="sm" h="36px" value={from} onChange={(e) => setFrom(e.target.value)} bg={crm.surface} />
                      </Box>
                      <Box>
                        <FieldLabel>Até</FieldLabel>
                        <Input type="date" size="sm" h="36px" value={to} onChange={(e) => setTo(e.target.value)} bg={crm.surface} />
                      </Box>
                    </>
                  )}
                  <Box>
                    <FieldLabel>Cliente</FieldLabel>
                    <select style={selectStyle} value={client} onChange={(e) => setClient(e.target.value)}>
                      <option value="">Todos</option>
                      {client && !data?.clientOptions.includes(client) && <option value={client}>{client}</option>}
                      {(data?.clientOptions ?? []).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Box>
                  <Box>
                    <FieldLabel>Agente comercial</FieldLabel>
                    <select style={selectStyle} value={agent} onChange={(e) => setAgent(e.target.value)}>
                      <option value="">Todos</option>
                      {agent && !data?.agentOptions.includes(agent) && <option value={agent}>{agent}</option>}
                      {(data?.agentOptions ?? []).map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </Box>
                  {data && (
                    <Text fontSize="sm" color={crm.textMuted} pb="8px">
                      {fmtDate(data.from)} a {fmtDate(data.to)}
                    </Text>
                  )}
                </Flex>

                {loading && !data ? (
                  <Flex justify="center" py="60px">
                    <Spinner color={crm.brand} />
                  </Flex>
                ) : !data ? (
                  <EmptyState title="Não foi possível carregar os dados." />
                ) : !data.hasData ? (
                  <Panel title="Faturamento de honorários">
                    <EmptyState title="Nenhum faturamento importado." hint={NO_DATA_HINT} />
                  </Panel>
                ) : (
                  <Box opacity={loading ? 0.6 : 1} transition="opacity .15s">
                    <Grid templateColumns={isMobile ? "1fr 1fr" : "repeat(6, 1fr)"} gap="16px" mb="20px">
                      <KpiTile title="Honorário bruto" value={brl0.format(data.kpis.gross)} />
                      <KpiTile title="Honorário líquido" value={brl0.format(data.kpis.net)} />
                      <KpiTile title="VMLD movimentado" value={brl0.format(data.kpis.vmld)} />
                      <KpiTile title="Processos" value={num.format(data.kpis.processes)} />
                      <KpiTile title="Clientes ativos" value={num.format(data.kpis.activeClients)} />
                      <KpiTile title="Ticket médio" value={brl0.format(data.kpis.averageTicket)} hint="Bruto ÷ processos" />
                    </Grid>

                    <Grid templateColumns={isMobile ? "1fr" : "3fr 2fr"} gap="16px" mb="16px">
                      <Panel title="Evolução mensal dos honorários" subtitle="Bruto e líquido por mês de faturamento">
                        <BarChart
                          height={280}
                          xAxis={[
                            {
                              data: data.monthly.map((m) => monthLabel(m.month)),
                              scaleType: "band",
                              tickLabelStyle: { fontSize: 11, fill: crm.textFaint },
                            },
                          ]}
                          yAxis={[{ tickLabelStyle: { fontSize: 11, fill: crm.textFaint }, valueFormatter: (v: number) => brl0.format(v) }]}
                          series={[
                            { data: data.monthly.map((m) => m.gross), label: "Bruto", color: crm.brand, valueFormatter: (v) => brl.format(v ?? 0) },
                            { data: data.monthly.map((m) => m.net), label: "Líquido", color: NET_COLOR, valueFormatter: (v) => brl.format(v ?? 0) },
                          ]}
                          margin={{ left: 90, right: 10, top: 20, bottom: 30 }}
                        />
                      </Panel>
                      <Panel title="Top 10 clientes" subtitle="Por honorário bruto">
                        {data.topClients.length === 0 ? (
                          <EmptyState title="Sem faturamento no período." />
                        ) : (
                          data.topClients.map((c) => (
                            <RankBar key={c.client} label={c.client} value={c.gross} max={maxTop} display={brl0.format(c.gross)} />
                          ))
                        )}
                      </Panel>
                    </Grid>

                    <Grid templateColumns={isMobile ? "1fr" : "1fr 1fr 1fr"} gap="16px" mb="16px">
                      <Panel title="Concentração da carteira" subtitle="Participação no honorário bruto">
                        {[
                          { label: "Maior cliente", value: data.concentration.top1 },
                          { label: "Top 5 clientes", value: data.concentration.top5 },
                          { label: "Top 10 clientes", value: data.concentration.top10 },
                        ].map((row) => (
                          <RankBar key={row.label} label={row.label} value={row.value} max={100} display={pctFmt(row.value)} />
                        ))}
                      </Panel>
                      <Panel title="Honorários por agente" subtitle="Bruto por agente comercial">
                        {data.byAgent.length === 0 ? (
                          <EmptyState title="Sem dados no período." />
                        ) : (
                          data.byAgent.map((a) => (
                            <RankBar
                              key={a.agent}
                              label={`${a.agent} (${a.clients} cli.)`}
                              value={a.gross}
                              max={maxAgent}
                              display={brl0.format(a.gross)}
                            />
                          ))
                        )}
                      </Panel>
                      <Panel
                        title="Movimentos da carteira"
                        subtitle={
                          data.movements.available
                            ? `Comparado a ${fmtDate(data.movements.previousFrom)} – ${fmtDate(data.movements.previousTo)}`
                            : undefined
                        }
                      >
                        {!data.movements.available ? (
                          <EmptyState title="Indisponível no histórico." hint="Escolha um período para comparar com o anterior." />
                        ) : (
                          <Grid templateColumns="1fr 1fr" gap="12px">
                            <MovementList title="Novos" tone={crm.success} items={data.movements.new} />
                            <MovementList title="Perdidos" tone={crm.danger} items={data.movements.lost} />
                          </Grid>
                        )}
                      </Panel>
                    </Grid>

                    <Panel
                      title="Desempenho por cliente"
                      subtitle="Vs ano anterior compara o bruto com o mesmo intervalo, um ano antes"
                      action={
                        <Button size="sm" variant="outline" leftIcon={<FaFileDownload />} onClick={exportPerformance} isDisabled={data.clients.length === 0}>
                          Exportar
                        </Button>
                      }
                    >
                      {data.clients.length === 0 ? (
                        <EmptyState title="Sem faturamento no período." />
                      ) : (
                        <Box overflowX="auto">
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>
                                {["Cliente", "Hon. bruto", "Hon. líquido", "VMLD", "Processos", "Ticket médio", "Part.", "Vs ano ant."].map((h, i) => (
                                  <th key={h} style={{ ...tableHeadStyle, textAlign: i === 0 ? "left" : "right" }}>
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {data.clients.map((c) => (
                                <tr key={c.client}>
                                  <td style={tableCellStyle}>{c.client}</td>
                                  <td style={{ ...tableCellStyle, textAlign: "right" }}>{brl.format(c.gross)}</td>
                                  <td style={{ ...tableCellStyle, textAlign: "right" }}>{brl.format(c.net)}</td>
                                  <td style={{ ...tableCellStyle, textAlign: "right" }}>{brl.format(c.vmld)}</td>
                                  <td style={{ ...tableCellStyle, textAlign: "right" }}>{num.format(c.processes)}</td>
                                  <td style={{ ...tableCellStyle, textAlign: "right" }}>{brl.format(c.averageTicket)}</td>
                                  <td style={{ ...tableCellStyle, textAlign: "right" }}>{pctFmt(c.participation)}</td>
                                  <td
                                    style={{
                                      ...tableCellStyle,
                                      textAlign: "right",
                                      color: c.vsPreviousYear === null ? crm.textFaint : c.vsPreviousYear >= 0 ? crm.success : crm.danger,
                                    }}
                                  >
                                    {c.vsPreviousYear === null ? "–" : `${c.vsPreviousYear >= 0 ? "+" : ""}${pctFmt(c.vsPreviousYear)}`}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      )}
                    </Panel>
                  </Box>
                )}
              </TabPanel>

              {/* ───────── Gestão de clientes ───────── */}
              <TabPanel p="0">
                {mgmtLoading ? (
                  <Flex justify="center" py="60px">
                    <Spinner color={crm.brand} />
                  </Flex>
                ) : !mgmt ? (
                  <EmptyState title="Não foi possível carregar os dados." />
                ) : (
                  <>
                    <Grid templateColumns={isMobile ? "1fr 1fr" : "repeat(4, 1fr)"} gap="16px" mb="20px">
                      <KpiTile title="Clientes na carteira" value={num.format(mgmt.total)} />
                      <KpiTile title="Com responsável" value={num.format(mgmt.withResponsible)} />
                      <KpiTile title="Com Agente 1" value={num.format(mgmt.withAgent1)} />
                      <KpiTile title="Com Agente 2" value={num.format(mgmt.withAgent2)} />
                    </Grid>
                    <Panel
                      title="Carteira de clientes"
                      action={
                        <Flex gap="8px" align="center">
                          <Input
                            size="sm"
                            h="36px"
                            w="220px"
                            placeholder="Buscar cliente, responsável, agente"
                            value={mgmtSearch}
                            onChange={(e) => setMgmtSearch(e.target.value)}
                            bg={crm.surface}
                          />
                          <Button size="sm" variant="outline" leftIcon={<FaFileDownload />} onClick={exportClients} isDisabled={mgmtRows.length === 0}>
                            Exportar
                          </Button>
                        </Flex>
                      }
                    >
                      {mgmtRows.length === 0 ? (
                        <EmptyState
                          title={mgmt.total === 0 ? "Nenhum cliente comercial cadastrado." : "Nenhum cliente encontrado."}
                          hint={mgmt.total === 0 ? "Cadastre em Comercial › Clientes Ativos." : undefined}
                        />
                      ) : (
                        <Box overflowX="auto">
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>
                                {["Cliente", "Comercial responsável", "Agente 1 (comissionado)", "Agente 2 (comissionado)", "Último faturamento"].map((h) => (
                                  <th key={h} style={tableHeadStyle}>
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {mgmtRows.map((r) => (
                                <tr key={r.companyId}>
                                  <td style={tableCellStyle}>{r.client}</td>
                                  <td style={tableCellStyle}>{r.commercialResponsible || "–"}</td>
                                  <td style={tableCellStyle}>{r.agent1 || "–"}</td>
                                  <td style={tableCellStyle}>{r.agent2 || "–"}</td>
                                  <td style={tableCellStyle}>{fmtDate(r.lastBilling)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      )}
                    </Panel>
                  </>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Layout>
    </Box>
  );
};

const MovementList = ({
  title,
  tone,
  items,
}: {
  title: string;
  tone: string;
  items: { client: string; gross: number; date: string }[];
}) => (
  <Box minW={0}>
    <Text fontSize="sm" fontWeight="700" color={tone} mb="6px">
      {title} ({items.length})
    </Text>
    {items.length === 0 ? (
      <Text fontSize="xs" color={crm.textFaint}>
        Nenhum.
      </Text>
    ) : (
      <Box maxH="200px" overflowY="auto">
        {items.map((i) => (
          <Box key={i.client} mb="6px">
            <Text fontSize="sm" color={crm.text} noOfLines={1} title={i.client}>
              {i.client}
            </Text>
            <Text fontSize="xs" color={crm.textMuted}>
              {brl0.format(i.gross)} · {fmtDate(i.date)}
            </Text>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

export default Comercial;
